import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import moment from "moment";
import { getMemberTrackerDetailApi } from "src/DAL/tracker/tracker";
import MemberTrackerForm from "./MemberTrackerForm";
import {
  canSendLate,
  getJourneySteps,
  getTrackerListMeta,
  isLeftTracker,
  isRemovedByStaff,
} from "./memberTrackersStorage";
import JourneyPasswordGate, {
  isJourneyPasswordRequired,
} from "./JourneyPasswordGate";

const MUTED = "#64748b";
const INK = "#0f172a";
const PRIMARY = "var(--portal-theme-primary, #04abbb)";

/** Dedicated portal check-in screen (not the public join page). */
export default function MemberTrackerSubmitPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  // ?step=<schedule step id>: a missed check-in sent late.
  const [searchParams] = useSearchParams();
  const stepParam = searchParams.get("step") || "";
  const [tracker, setTracker] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [loadNotFound, setLoadNotFound] = useState(false);
  const [lockedTracker, setLockedTracker] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadState("loading");
      try {
        const res = await getMemberTrackerDetailApi(id);
        if (cancelled) return;
        if (res?.code === 200 && res.tracker) {
          const subs = (res.submissions || []).map((row) => ({
            ...row,
            id: String(row._id || row.id),
          }));
          setTracker({
            ...res.tracker,
            _memberSubmissions: subs,
          });
          setSubmissions(subs);
          setLoadState("ready");
        } else if (isJourneyPasswordRequired(res)) {
          setTracker(null);
          setSubmissions([]);
          setLockedTracker(res.tracker || {});
          setLoadState("locked");
        } else {
          setTracker(null);
          setSubmissions([]);
          setLoadNotFound(res?.code === 404);
          setLoadState("error");
        }
      } catch (_e) {
        if (cancelled) return;
        setTracker(null);
        setSubmissions([]);
        setLoadNotFound(false);
        setLoadState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, reloadKey]);

  const meta = useMemo(
    () =>
      tracker
        ? getTrackerListMeta({ ...tracker, _memberSubmissions: submissions })
        : null,
    [tracker, submissions]
  );

  const lateStep = useMemo(() => {
    if (!tracker || !stepParam) return null;
    const step = getJourneySteps(tracker, submissions).find(
      (item) => item.id === stepParam
    );
    return step && step.checkInId && canSendLate(step, tracker) ? step : null;
  }, [tracker, submissions, stepParam]);

  const goBack = () => navigate(`/trackers/${id}`);

  if (loadState === "loading") {
    return (
      <div className="container-fluid">
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      </div>
    );
  }

  if (loadState === "locked") {
    return (
      <JourneyPasswordGate
        idOrSlug={id}
        tracker={lockedTracker}
        onUnlocked={() => {
          setLockedTracker(null);
          setReloadKey((key) => key + 1);
        }}
      />
    );
  }

  if (!tracker) {
    return (
      <div className="container-fluid">
        <button
          type="button"
          className="small-contained-button mb-3"
          onClick={() => navigate("/trackers")}
        >
          Back to Journeys
        </button>
        <Typography sx={{ fontWeight: 700, color: INK }}>
          {loadNotFound ? "Journey not found" : "Couldn't load this journey"}
        </Typography>
        <Typography sx={{ color: MUTED, mt: 0.5 }}>
          {loadNotFound
            ? "This journey may have been removed or you no longer have access."
            : "Something went wrong while loading it. Please try again."}
        </Typography>
        {!loadNotFound ? (
          <button
            type="button"
            className="small-contained-outlined-button mt-3"
            onClick={() => setReloadKey((key) => key + 1)}
          >
            Try again
          </button>
        ) : null}
      </div>
    );
  }

  // Left members may always submit — that's how they rejoin (backend clears "left").
  // Removed by a coach / admin: only staff can put them back.
  const removed = isRemovedByStaff(tracker);
  if (removed || (!lateStep && !meta?.canSubmit && !isLeftTracker(tracker))) {
    const locked = removed ? null : meta?.lockedStep;
    return (
      <div className="container-fluid">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2.5,
            cursor: "pointer",
            width: "fit-content",
            color: MUTED,
            "&:hover": { color: PRIMARY },
          }}
          onClick={goBack}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>
            Back to {tracker.name}
          </Typography>
        </Box>
        <Box sx={{ width: "100%", py: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 22, color: INK, mb: 1 }}>
            {removed
              ? "You were removed from this journey"
              : // Dates, not "Check-in 3" — the line below says when it opens.
                (locked?.kind !== "check_in" && locked?.name) || "Not open yet"}
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: 14.5, lineHeight: 1.55, mb: 2.5 }}>
            {removed
              ? "Please contact your coach to rejoin."
              : locked?.unlockAt
                ? `Come back on ${moment(locked.unlockAt).format(
                    "D MMM YYYY"
                  )} to continue your journey.`
                : "There is no check-in available right now."}
          </Typography>
          <button
            type="button"
            className="small-contained-button"
            onClick={goBack}
          >
            Back to journey
          </button>
        </Box>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 3,
          cursor: "pointer",
          width: "fit-content",
          color: MUTED,
          "&:hover": { color: PRIMARY },
        }}
        onClick={goBack}
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
        <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>
          Back to {tracker.name}
        </Typography>
      </Box>

      <Box sx={{ pb: 4, width: "100%" }}>
        <MemberTrackerForm
          tracker={tracker}
          lateStep={lateStep}
          onCancel={goBack}
          onSuccess={() => {
            // Late: back on the check-ins tab with that one open.
            navigate(
              lateStep
                ? `/trackers/${id}?tab=checkins&step=${encodeURIComponent(lateStep.id)}`
                : `/trackers/${id}`,
              { replace: true, state: { submitted: true } }
            );
          }}
        />
      </Box>
    </div>
  );
}
